import React, { useCallback, useEffect, useState } from "react";
import Zoom from "react-medium-image-zoom";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { updateCart } from "../../actions/cart";
import { setMeta } from "../../actions/meta";
import { getReviews } from "../../actions/products";
import { showErrorSnackbar } from "../../actions/snackbar";
import { getItemList, getProduct } from "../../api/items";
import { COLORS } from "../../constant/colors";
import { UPDATE_CART } from "../../constants/actionTypes";
import { findProductInCart, updateCartData } from "../../helpers/cart";
import { convertToSlug, dataUrl, toArray } from "../../helpers/common";
import AddCartButton from "../AddCartButton";
import MetaTags from "../Common/MetaTags";
import ProductLayout from "../ProductLayout";
import ProductAccordion from "./ProductAccordion";
import SimilarProducts from "./SimilarProducts";
import SingleProductDetailMeta from "./SingleProductDetailMeta";
import useStyles from "./styles";
import ProductReview from "./ProductReview";

const SingleProduct = () => {
  const classes = useStyles();

  let { productId } = useParams();

  const [productVariants, setProductVariants] = useState([]);
  const currProduct = useSelector((state) => state.products.product);
  const [product, setProduct] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentImage, setCurrentImage] = useState("");
  const [currentImageFull, setCurrentImageFull] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [thumbs, setThumbs] = useState([]);
  const dispatch = useDispatch();
  const cartData = useSelector((state) => state.cart.data);
  const navigate = useNavigate();
  const tabRef = React.useRef();

  // Set current image when product changes
  useEffect(() => {
    if (product?.main_image) {
      setCurrentImage(
        `${dataUrl}/products/${product?.id}/500x438-${product?.main_image}`
      );
      setCurrentImageFull(
        `${dataUrl}/products/${product?.id}/${product?.main_image}`
      );
    } else {
      setCurrentImage("/images/placeholder-img.jpg");
    }
  }, [product]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

    if (product?.id) {
      dispatch(getReviews(product?.id));
    }
  }, [product, dispatch]);

  useEffect(() => {
    setLoading(true);
    getProduct(productId)
      .then((result) => {
        setProduct(result.data);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });
  }, [productId]);

  useEffect(() => {
    setProduct(currProduct);
  }, [currProduct]);

  const changeProduct = useCallback(
    (id) => {
      setLoading(true);
      getProduct(id)
        .then((result) => {
          setProduct(result.data);
          setLoading(false);
        })
        .catch((err) => {
          console.log(err);
          setLoading(false);
        });
    },
    [setProduct]
  );

  const asyncCallsOnLoad = useCallback(async () => {
    try {
      const data = product;

      let media = data.media;

      if (media) {
        const thumbs = [];

        let mediaArr = toArray(media, ",");

        mediaArr.forEach((m) => {
          thumbs.push({
            img: m,
          });
        });

        setThumbs(thumbs);
      }
    } catch (err) {
      console.log(err);
      console.log(err.response);
    }
  }, [product]);

  useEffect(() => {
    asyncCallsOnLoad();
  }, [asyncCallsOnLoad]);

  const changeImage = (image) => {
    setCurrentImage(`${dataUrl}/products/${product?.id}/500x438-${image.img}`);
    setCurrentImageFull(`${dataUrl}/products/${product?.id}/${image.img}`);
  };

  // finding parent from category id
  const categories = useSelector((state) => state.categories.data);

  const findCat = categories.find((cat) => cat.id === product?.category_id);
  const findMainCat = categories.find((cat) => cat.id === findCat?.parent_id);

  useEffect(() => {
    if (product?.name) {
      let breadcrumb = (
        <div className={classes.breadcrumb}>
          <Link to="/">
            <i className="fa fa-home" style={{ color: COLORS.primary }} />
          </Link>{" "}
          /
          {findMainCat?.name && findMainCat?.name?.toLowerCase() !== "main" ? (
            <> {findMainCat?.name} / </>
          ) : null}
          <Link
            className={classes.breadcrumbLink}
            to={`/category/${product?.category_id}/${convertToSlug(
              product?.category_name
            )}/1`}
          >
            {product?.category_name}
          </Link>{" "}
          / {product?.name}{" "}
        </div>
      );
      dispatch(setMeta({ title: "Product", breadcrumb }));
    }
  }, [
    classes.breadcrumb,
    classes.breadcrumbLink,
    dispatch,
    findMainCat,
    product,
  ]);

  useEffect(() => {
    let isCancelled = false;
    setProductVariants([]);
    if (!isCancelled) {
      getItemList(
        "products",
        `ProductsSearch[variants_of]=${product?.id}&sort=item_weight`
      ).then((json) => setProductVariants(json?.data));
    }
    return () => {
      isCancelled = true;
    };
  }, [product, dispatch]);

  const updateCartProduct = useCallback(
    (product, action) => {
      let newQty = quantity;
      // find product in cart
      let cartProduct = findProductInCart(cartData, product);

      if (action === "increase") {
        newQty = quantity + 1;
      }

      if (action === "decrease") {
        newQty = quantity - 1;
      }

      // atlease one qty must be there in cart.
      if (newQty === 0) {
        return;
      }

      // customer can not add more products than the available stock.
      if (newQty > product?.available_stock) {
        dispatch(
          showErrorSnackbar(
            `No more stock is available for the Product '${product?.name}'`
          )
        );
        return;
      }

      setQuantity(newQty);
      if (cartProduct) {
        const newCartData = updateCartData(cartData, product, newQty);
        dispatch({ type: UPDATE_CART, payload: newCartData });
      }
    },
    [quantity, cartData, dispatch]
  );

  useEffect(() => {
    let quantity = findProductInCart(cartData, product);
    if (quantity?.qty) {
      setQuantity(quantity.qty);
    } else {
      setQuantity(1);
    }
  }, [setQuantity, cartData, product]);

  const handleBuyNow = useCallback(
    (product) => {
      if (
        product?.available_stock < quantity ||
        product.available_stock <= 0 ||
        product.selling_price === 0
      ) {
        dispatch(
          showErrorSnackbar(
            `No more stock is available for the Product '${product?.name}'`
          )
        );
        return;
      }
      const newCartData = updateCartData(cartData, product, quantity);
      dispatch(updateCart(newCartData));
      navigate("/cart");
    },
    [quantity, cartData, dispatch, navigate]
  );

  const loadMoreWeight = () => {
    if (productVariants.length === 0) {
      return null;
    }

    return (
      <>
        {productVariants[0]?.item_weight === 0 ? null : (
          <div className={classes.weightSec}>
            <p className={classes.weight}>Weight</p>
            <div className={classes.btnRow}>
              {productVariants?.map((variant) => (
                <div
                  className={classes.products}
                  onClick={(_) => changeProduct(variant.id)}
                  key={"variant-" + variant.id}
                >
                  <div className={classes.btnTab}>
                    <Link
                      className={`${classes.weightBtn} ${
                        variant.id === product?.id
                          ? classes.weightBtnActive
                          : ""
                      }`}
                      to="#"
                    >
                      {variant?.item_weight} gms
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </>
    );
  };

  let singleProduct = (
    <div className="col-12">
      <div className="row">
        <div className="col-xl-6 col-lg-6 col-md-12 col-sm-12">
          <div className={classes.productImageSection}>
            <div className={classes.mainImageContainer}>
              <div className={classes.imageWrapper}>
                <Zoom
                  zoomImg={{
                    src: currentImageFull,
                    alt: product?.name,
                    style: {
                      maxHeight: "100vh",
                      width: "auto",
                      objectFit: "contain",
                    },
                  }}
                >
                  <img
                    src={currentImage}
                    alt={product?.name}
                    className={classes.mainImage}
                    onError={(e) => {
                      e.target.src = "/images/placeholder-img.jpg";
                    }}
                  />
                </Zoom>
                <div className={classes.zoomIcon}>
                  <i className="fa fa-search-plus" aria-hidden="true"></i>
                </div>
              </div>
            </div>
            {thumbs.length > 0 && (
              <div className={classes.thumbnailContainer}>
                {thumbs.map((thumb, index) => (
                  <div key={"thumb-" + index} className={classes.thumbnailItem}>
                    <img
                      key={"thumb" + thumb.img}
                      onClick={() => changeImage(thumb)}
                      src={`${dataUrl}/products/${product?.id}/120x120-${thumb.img}`}
                      alt={product?.name}
                      className={`${classes.thumbnail} ${
                        currentImage.includes(thumb.img)
                          ? classes.activeThumbnail
                          : ""
                      }`}
                      onError={(e) => {
                        e.target.src = "/images/placeholder-img.jpg";
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="col-xl-6 col-lg-6 col-md-12 col-sm-12">
          <div className={classes.productDetailsSection}>
            <div className={classes.productHeader}>
              <h1 className={classes.productName}>{product?.name}</h1>

              <div className={classes.priceSection}>
                {product?.mrp > product?.selling_price && (
                  <div className={classes.originalPrice}>
                    <span className={classes.currency}>₹</span>
                    <span className={classes.mrpPrice}>{product?.mrp}</span>
                  </div>
                )}
                <div className={classes.currentPrice}>
                  <span className={classes.currency}>₹</span>
                  <span className={classes.sellingPrice}>
                    {product?.selling_price}
                  </span>
                </div>
                {product?.mrp > product?.selling_price && (
                  <div className={classes.discountBadge}>
                    {Math.round(
                      ((product?.mrp - product?.selling_price) / product?.mrp) *
                        100
                    )}
                    % OFF
                  </div>
                )}
              </div>
            </div>

            <div className={classes.productInfo}>
              <div className={classes.infoItem}>
                <span className={classes.infoLabel}>SKU:</span>
                <span className={classes.infoValue}>{product?.sku}</span>
              </div>

              {/* <div className={classes.infoItem}>
                <span className={classes.infoLabel}>Availability:</span>
                <span
                  className={`${classes.stockStatus} ${
                    product?.available_stock > 0 && product.selling_price !== 0
                      ? classes.inStock
                      : classes.outOfStock
                  }`}
                >
                  {product?.available_stock > 0 && product.selling_price !== 0
                    ? "In Stock"
                    : "Out of Stock"}
                </span>
              </div> */}
            </div>

            <div className={classes.weightSection}>{loadMoreWeight()}</div>

            <div className={classes.quantitySection}>
              <div className={classes.quantityLabel}>Select Quantity</div>
              <div className={classes.quantityControls}>
                <button
                  className={classes.quantityBtn}
                  onClick={() => updateCartProduct(product, "decrease")}
                  disabled={quantity <= 1}
                >
                  −
                </button>
                <span className={classes.quantityValue}>{quantity}</span>
                <button
                  className={classes.quantityBtn}
                  onClick={() => updateCartProduct(product, "increase")}
                >
                  +
                </button>
              </div>
            </div>

            <div className={classes.actionButtons}>
              <div className={`${classes.buyNowBtnSec} btn-sec`}>
                <button
                  className={`my-btn ${classes.buyNow}`}
                  onClick={() => handleBuyNow(product)}
                >
                  Buy Now
                </button>
              </div>

              <div className={classes.addCartButton}>
                <AddCartButton
                  cartData={cartData}
                  product={product}
                  quantity={quantity}
                />
              </div>
            </div>

            {!loading ? (
              <>
                {/* Payment Partners Section */}
                <div className={classes.paymentPartnersSection}>
                  <h3 className={classes.paymentPartnersTitle}>
                    Payment Partners
                  </h3>
                  <img
                    src="/images/payment-parteners.jpg"
                    alt="Payment Partners"
                    className={classes.paymentPartnersImage}
                  />
                </div>

                <ProductAccordion product={product} tabRef={tabRef} />
              </>
            ) : null}
          </div>
        </div>
        {/* <ProductsReview /> */}
      </div>

      <SimilarProducts product={product} />

      {!loading && <ProductReview product={product} setProduct={setProduct} />}
    </div>
  );

  return (
    <ProductLayout
      hideFilterSec={true}
      parentClass="product_detail"
      categoryId={product?.category_id}
      showSidebar={false}
    >
      <SingleProductDetailMeta product={product} />

      {product?.name ? (
        <MetaTags
          module="products"
          title={product?.name}
          description={product?.description || product?.highlights}
          keywords={product?.search_tags}
          image={
            dataUrl + "/products/" + product?.id + "/" + product?.main_image
          }
        ></MetaTags>
      ) : null}

      {loading ? (
        <div className="col-xl-9 col-lg-9 col-md-8 col-sm-12 ">
          <div className={classes.loaderSec}>
            <img
              className={classes.loader}
              src="/images/loader1.gif"
              alt="loader"
            />
          </div>
        </div>
      ) : (
        singleProduct
      )}
    </ProductLayout>
  );
};

export default SingleProduct;
